import { cn } from "@d12labs/utils";
import type * as React from "react";

/**
 * Linear-dense status badge. A rounded-full pill with a hairline border and a
 * tinted fill keyed to the tone. Optional leading dot (a solid color) or icon.
 *
 * Tones map to the design's semantic palette; `primary` uses the lavender
 * accent. Keep `dot` to a hex/CSS color string for the small status dot.
 */
type BadgeTone = "neutral" | "subtle" | "primary" | "success" | "warning" | "danger";

const TONE: Record<BadgeTone, string> = {
	neutral: "bg-surface-2 text-ink-muted border-hairline",
	subtle: "bg-surface-2 text-ink-muted border-hairline",
	primary: "bg-primary/10 text-primary-hover border-primary/30",
	success: "bg-semantic-success/10 text-semantic-success border-semantic-success/25",
	warning: "bg-warning/10 text-warning border-warning/25",
	danger: "bg-destructive/10 text-destructive border-destructive/25",
};

export function Badge({
	tone = "neutral",
	dot,
	icon: Icon,
	className,
	children,
}: {
	tone?: BadgeTone;
	/** CSS color for the leading status dot. */
	dot?: string;
	icon?: React.ComponentType<{ className?: string }>;
	className?: string;
	children: React.ReactNode;
}) {
	return (
		<span
			data-slot="badge"
			className={cn(
				"inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-meta font-medium leading-none",
				TONE[tone],
				className,
			)}
		>
			{dot ? <span className="size-2 flex-none rounded-full" style={{ background: dot }} /> : null}
			{Icon ? <Icon className="size-3" /> : null}
			{children}
		</span>
	);
}

export type { BadgeTone };
